import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/Share';
import {
  alpha,
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Fade,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';

export interface ImageCardMenuItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  divider?: boolean;
  color?: 'inherit' | 'error' | 'warning' | 'success';
}

export interface ImageCardProps {
  image?: string;
  imageAlt?: string;
  title: string;
  description: string;
  icon?: React.ReactNode;
  menuItems?: ImageCardMenuItem[];
  tags?: string[];
  subtitle?: string;
  onCardClick?: () => void;
  favoriteCount?: number;
  shareCount?: number;
  onFavorite?: () => void;
  onShare?: () => void;
  isFavorited?: boolean;
  elevation?: number;
  imageHeight?: number | string;
  maxDescriptionLines?: number;
  width?: number | string;
}

const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: 16,
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.8)
    : theme.palette.background.paper,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${theme.palette.mode === 'dark'
    ? alpha(theme.palette.divider, 0.1)
    : alpha(theme.palette.divider, 0.08)}`,
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 12px 40px ${alpha(theme.palette.common.black, 0.5)}`
      : `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
    '& .card-image': {
      transform: 'scale(1.05)',
    },
    '& .card-overlay': {
      opacity: 1,
    },
  },
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
}));

const ImageOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(to bottom, ${alpha(theme.palette.common.black, 0.1)} 0%, ${alpha(theme.palette.common.black, 0.4)} 100%)`,
  opacity: 0,
  transition: 'opacity 0.3s ease',
  display: 'flex',
  alignItems: 'flex-end',
  padding: theme.spacing(2),
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(10px)',
  '&:hover': {
    backgroundColor: alpha(theme.palette.background.paper, 1),
    transform: 'scale(1.1)',
  },
  transition: 'all 0.2s ease',
  zIndex: 2,
}));

const IconContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  left: 16,
  width: 48,
  height: 48,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(10px)',
  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
  zIndex: 2,
  '& .MuiSvgIcon-root': {
    fontSize: '1.5rem',
    color: theme.palette.primary.main,
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(10px)',
  width: 36,
  height: 36,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    transform: 'scale(1.1)',
  },
  transition: 'all 0.2s ease',
}));

const ImageCard: React.FC<ImageCardProps> = ({
  image,
  imageAlt = '',
  title,
  description,
  icon,
  menuItems = [],
  tags = [],
  subtitle,
  onCardClick,
  favoriteCount,
  shareCount,
  onFavorite,
  onShare,
  isFavorited = false,
  elevation = 0,
  imageHeight = 320,
  maxDescriptionLines = 3,
  width,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [localFavorited, setLocalFavorited] = useState(isFavorited);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (menuItem: ImageCardMenuItem) => {
    menuItem.onClick();
    handleMenuClose();
  };

  const handleFavoriteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setLocalFavorited(!localFavorited);
    if (onFavorite) {
      onFavorite();
    }
  };

  const handleShareClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onShare) {
      onShare();
    }
  };

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick();
    }
  };

  return (
    <StyledCard
      elevation={elevation}
      onClick={handleCardClick}
      sx={{ cursor: onCardClick ? 'pointer' : 'default', width: width }}
    >
      {/* Image Section */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <StyledCardMedia
          className="card-image"
          image={image}
          title={imageAlt || title}
          sx={{
            height: imageHeight,
            objectFit: "cover",
          }}
        >
          {
            !image &&
            icon && (
              <Box
                sx={{
                  height: imageHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(10px)',
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                {icon}
              </Box>
            )
          }
        </StyledCardMedia>
        <ImageOverlay className="card-overlay" />

        {/* Icon */}
        {image && icon && (
          <Fade in timeout={300}>
            <IconContainer>{icon}</IconContainer>
          </Fade>
        )}

        {/* Menu Button */}
        {menuItems.length > 0 && (
          <MenuButton
            aria-label="more options"
            aria-controls={menuOpen ? 'card-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={handleMenuClick}
            size="small"
          >
            <MoreVertIcon />
          </MenuButton>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              right: 12,
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              zIndex: 2,
            }}
          >
            {tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(10px)',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 24,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Content Section */}
      <CardContent sx={{ p: 2.5 }}>
        {/* Subtitle */}
        {subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mb: 0.5,
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Title */}
        <Typography
          variant="h6"
          component="h3"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: '1.1rem',
            lineHeight: 1.3,
            mb: 1,
            color: theme.palette.mode === 'dark'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          }}
        >
          {title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            display: '-webkit-box',
            WebkitLineClamp: maxDescriptionLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>

        {/* Action Buttons */}
        {(onFavorite || onShare) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pt: 1,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            {onFavorite && (
              <Tooltip title={localFavorited ? 'Remove from favorites' : 'Add to favorites'}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ActionButton
                    size="small"
                    onClick={handleFavoriteClick}
                    sx={{
                      color: localFavorited ? theme.palette.error.main : 'inherit',
                    }}
                  >
                    {localFavorited ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                  </ActionButton>
                  {favoriteCount !== undefined && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {favoriteCount}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            )}

            {onShare && (
              <Tooltip title="Share">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ActionButton size="small" onClick={handleShareClick}>
                    <ShareIcon fontSize="small" />
                  </ActionButton>
                  {shareCount !== undefined && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {shareCount}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            )}
          </Box>
        )}
      </CardContent>

      {/* Menu */}
      <Menu
        id="card-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: 2,
            minWidth: 180,
            mt: 0.5,
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.95)
              : theme.palette.background.paper,
            backdropFilter: 'blur(10px)',
          },
        }}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.id}
            onClick={() => handleMenuItemClick(item)}
            divider={item.divider}
            sx={{
              py: 1.5,
              color: item.color === 'error' ? 'error.main' : 'inherit',
              '&:hover': {
                backgroundColor: item.color === 'error'
                  ? alpha(theme.palette.error.main, 0.1)
                  : alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {item.icon && (
              <ListItemIcon
                sx={{
                  color: item.color === 'error' ? 'error.main' : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </StyledCard>
  );
};

export default ImageCard;
